<template>
  <div v-show="state.visible" class="footer" role="navigation" aria-label="Footer">
    <div
      class="footer__inner"
    >
      <AppLogo class="footer__logo" />
      <ol class="footer__menu-items">
        <li
          v-for="(menu_item, index) in settingsStore.getNavigation.footer_menu"
          :key="`menu-item_${index}`"
          class="footer__item"
        >
          <NuxtLink :href="pageOrUrl(menu_item.item)" class="footer__link">
            {{ menu_item.item.label }}
          </NuxtLink>
        </li>
      </ol>
      <ol class="footer__social-items">
        <li>
          <NuxtLink
            href="https://www.facebook.com/AVROTROS/"
            aria-label="facebook"
            target="_blank"
            rel="noopener noreferrer"
            class="footer__item-facebook-link"
          >
            <AppIconLoader name="facebook" variant="medium" />
            <span class="footer__label">Facebook</span>
          </NuxtLink>
        </li>
        <li>
          <NuxtLink
            href="https://www.instagram.com/avrotros/"
            aria-label="instagram"
            target="_blank"
            rel="noopener noreferrer"
            class="footer__item-facebook-link"
          >
            <AppIconLoader name="instagram" variant="medium" />
            <span class="footer__label">Instagram</span>
          </NuxtLink>
        </li>
        <li>
          <NuxtLink
            href="https://www.youtube.com/avrotros"
            aria-label="youtube"
            target="_blank"
            rel="noopener noreferrer"
            class="footer__item-facebook-link"
          >
            <AppIconLoader name="youtube" variant="medium" />
            <span class="footer__label">Youtube</span>
          </NuxtLink>
        </li>
        <li>
          <NuxtLink
            href="https://www.tiktok.com/@avrotros"
            aria-label="tiktok"
            target="_blank"
            rel="noopener noreferrer"
            class="footer__item-facebook-link"
          >
            <AppIconLoader name="tiktok" variant="medium" />
            <span class="footer__label">TikTok</span>
          </NuxtLink>
        </li>
      </ol>
      <ol class="footer__contact-items">
        <li
          v-for="(menu_item, index) in settingsStore.getNavigation.footer_menu_contact"
          :key="`contact-item_${index}`"
          class="footer__item"
        >
          <NuxtLink :href="pageOrUrl(menu_item.item)" class="footer__link">
            {{ menu_item.item.label }}
          </NuxtLink>
        </li>
      </ol>
      <ol class="footer__legal-items">
        <li
          v-for="(menu_item, index) in settingsStore.getNavigation.footer_menu_legal"
          :key="`legal-item_${index}`"
          class="footer__item"
        >
          <NuxtLink :href="pageOrUrl(menu_item.item)" class="footer__link">
            {{ menu_item.item.label }}
          </NuxtLink>
        </li>
      </ol>
      <div
        class="footer__content"
      >
        <NuxtLink
          aria-label="Ga naar NPO Start"
          href="https://www.npostart.nl/?ns_campaign=meernpo&ns_mchannel=omroep&ns_source=avrotros&ns_linkname=button"
          target="_blank"
          rel="noopener noreferrer"
          class="footer__content-link"
        >
          <AppLogoNpoStart class="footer__logo-npo-start" />
          <span class="footer__label">Ga naar NPO Start</span>
        </NuxtLink>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { onMounted } from 'vue'
import { onBeforeRouteLeave } from 'vue-router'
import { useSettingsStore } from '~/store/settings'
import { TMenuItem } from '~/types/other-types'
import { joinSlugId } from '~/utils/slug-id'

const settingsStore = useSettingsStore()

const state = reactive({
  visible: false
})

const pageOrUrl = (item: TMenuItem) => {
  const page = item.page[0]?.item
  const collection = item.page[0]?.collection
  if (page && collection) {
    const pageType = collection.split('_page')[0]
    const slugId = joinSlugId(page)
    if (pageType === 'content') {
      if (page.belongs_to?.slug) {
        return `/${pageType}-${page.belongs_to?.slug}/${slugId}/`
      } else {
        return `/${slugId}/`
      }
    }
    if (pageType === 'articles') {
      return `/archive/${slugId}/`
    } else {
      return `/${pageType}/${slugId}/`
    }
  } else {
    return item.url
  }
}

onBeforeRouteLeave(() => {
  state.visible = false
})

onMounted(() => {
  window.setTimeout(() => {
    state.visible = true
  }, 1000)
})

</script>

<style scoped lang="postcss">
.footer {
  position: relative;
  background-color: var(--color-blue-aqua);
  padding-bottom: 2.5rem;
  box-shadow: 0 0 9px 0 #00000040;

  &__inner {
    @mixin container;
    font-family: GraphikWide, sans-serif;
    font-weight: 700;
    line-height: 1.2;
    color: var(--color-blue-prussian);
    @mixin app-container-padding;
    @mixin app-grid;

    @media (--lg) {
      font-size: 1.125rem;
      line-height: 1.75rem;
    }
  }

  &__logo {
    grid-column: span 6 / span 6;
    grid-column-start: 4;
    margin-left: 2.5rem;
    margin-right: 2.5rem;
    margin-top: 2.5rem;
    margin-bottom: 0.75rem;

    @media (--md) {
      margin-bottom: 1.5rem;
      margin-top: 5rem;
    }

    @media (--lg) {
      grid-column: span 3 / span 3;
      margin-left: 0px;
      margin-right: 0px;
      margin-top: 2.5rem;
    }

    @media (--xl) {
      margin-left: 2.5rem;
      margin-right: 2.5rem;
    }
  }

  &__menu-items {
    grid-column: 1 / -1;

    @media (--lg) {
      grid-column: span 3 / span 3;
      margin-top: 1.25rem;
    }
  }

  &__item {
    margin-top: 1.25rem;
  }

  &__link {
    display: block;
    text-align: center;
    @mixin focus-ring;
  }

  &__social-items {
    grid-column: 1 / -1;
    margin-top: 1.25rem;
    display: flex;
    justify-content: center;

    @media (--lg) {
      order: 1;
      grid-column: span 3 / span 3;
      margin-top: 3rem;
    }
  }

  &__item-facebook-link {
    margin-left: 1.25rem;
    margin-right: 1.25rem;
    display: block;
    @mixin focus-ring;

    @media (--lg) {
      margin-left: 0.5rem;
      margin-right: 0.5rem;
    }
  }

  &__label {
    @mixin sr-only;
  }

  &__contact-items {
    grid-column: 1 / -1;

    @media (--lg) {
      grid-column: span 3 / span 3;
      margin-top: 1.25rem;
    }
  }

  &__legal-items {
    grid-column: 1 / -1;

    @media (--lg) {
      grid-column: span 3 / span 3;
      margin-top: 1.25rem;
    }
  }

  &__content {
    grid-column: 1 / -1;
    margin-top: 1.25rem;
    display: flex;
    justify-content: space-around;

    @media (--md) {
      margin-top: 2.5rem;
    }

    @media (--lg) {
      order: 1;
      grid-column: span 3 / span 3;
      grid-column-start: 10;
      justify-self: end;
    }
  }

  &__content-link {
    margin-left: 1.25rem;
    margin-right: 1.25rem;
    display: block;
    @mixin focus-ring;

    @media (--lg) {
      margin-left: 0.5rem;
      margin-right: 0.5rem;
    }
  }

  &__logo-npo-start {
    height: 56px;
  }
}
</style>
